import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../firebase';
import styled from 'styled-components';

const getErrorMessage = (errorCode) => {
  switch (errorCode) {
    case 'auth/email-already-in-use':
      return 'An account with this email already exists. Please sign in or use a different email.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/operation-not-allowed':
      return 'Email/password accounts are not enabled. Please contact support.';
    case 'auth/weak-password':
      return 'Password is too weak. Please use at least 6 characters with a mix of letters and numbers.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your internet connection.';
    default:
      return 'Unable to submit request. Please try again.';
  }
};

const Container = styled.div`
  max-width: 480px;
  margin: 40px auto;
  padding: 32px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
  font-size: 1.5rem;
  color: #1a1a2e;
  margin: 0 0 8px 0;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 0.9rem;
  color: #666;
  text-align: center;
  margin: 0 0 24px 0;
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 0.85rem;
  color: #444;
  font-weight: 500;
`;

const Input = styled.input`
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.95rem;

  &:focus {
    outline: none;
    border-color: #2d5a27;
  }

  &::placeholder {
    color: #aaa;
  }
`;

const OptionalLabel = styled.span`
  color: #888;
  font-weight: 400;
  font-size: 0.8rem;
`;

const Button = styled.button`
  padding: 14px;
  background: #2d5a27;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
  margin-top: 8px;

  &:hover {
    background: #1f3d1b;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const Error = styled.div`
  color: #dc3545;
  font-size: 0.85rem;
  text-align: center;
  background: #fff5f5;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #ffebee;
`;

const LoginLink = styled.div`
  text-align: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #eee;
  font-size: 0.85rem;
  color: #666;

  a {
    color: #2d5a27;
    text-decoration: none;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Notice = styled.div`
  background: #f8f9fa;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  padding: 12px;
  font-size: 0.8rem;
  color: #666;
  line-height: 1.5;
`;

// Success/Pending state
const SuccessContainer = styled.div`
  max-width: 480px;
  margin: 60px auto;
  padding: 40px 32px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const SuccessIcon = styled.div`
  width: 64px;
  height: 64px;
  background: #e8f5e9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px auto;
  font-size: 1.5rem;
  color: #2d5a27;
  font-weight: bold;
`;

const SuccessTitle = styled.h2`
  font-size: 1.25rem;
  color: #1a1a2e;
  margin: 0 0 12px 0;
`;

const SuccessText = styled.p`
  font-size: 0.9rem;
  color: #666;
  line-height: 1.6;
  margin: 0 0 24px 0;
`;

const SuccessDetails = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  text-align: left;
  font-size: 0.85rem;
  color: #555;
  line-height: 1.6;

  strong {
    color: #1a1a2e;
  }
`;

const RegisterForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    propertyAddress: '',
    entityName: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Create Firebase user (with pending status)
      await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      // Store the access request data in localStorage for now
      // In production, this would go to your backend
      localStorage.setItem('accessRequest', JSON.stringify({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        propertyAddress: formData.propertyAddress,
        entityName: formData.entityName,
        status: 'pending',
        submittedAt: new Date().toISOString()
      }));

      // Sign out immediately (they can't access until approved)
      await auth.signOut();

      setSubmitted(true);
    } catch (err) {
      console.error('Registration error:', err.code, err.message);
      setError(getErrorMessage(err.code));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <SuccessContainer>
        <SuccessIcon>...</SuccessIcon>
        <SuccessTitle>Account Verification Pending</SuccessTitle>
        <SuccessText>
          Thank you for requesting access to ShowingBooker. We verify property ownership
          to ensure our platform is used by legitimate property owners and managers.
        </SuccessText>
        <SuccessDetails>
          <strong>What happens next:</strong><br />
          Our team will verify your property ownership and review your application.
          You will receive an email at <strong>{formData.email}</strong> within
          48 hours with your account status.<br /><br />
          <strong>Questions?</strong> Contact us at david@codexdeus.com
        </SuccessDetails>
      </SuccessContainer>
    );
  }

  return (
    <Container>
      <Title>Request Access</Title>
      <Subtitle>
        ShowingBooker is for verified property owners and managers.
        Complete the form below and we'll review your application within 48 hours.
      </Subtitle>

      <Form onSubmit={handleSubmit}>
        <FormRow>
          <FormGroup>
            <Label>First Name</Label>
            <Input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="John"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Last Name</Label>
            <Input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Smith"
              required
            />
          </FormGroup>
        </FormRow>

        <FormGroup>
          <Label>Email</Label>
          <Input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="john@example.com"
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>Phone</Label>
          <Input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="(555) 123-4567"
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>Main Property Address</Label>
          <Input
            type="text"
            name="propertyAddress"
            value={formData.propertyAddress}
            onChange={handleChange}
            placeholder="123 Main St, Los Angeles, CA 90001"
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>Entity Name <OptionalLabel>(optional)</OptionalLabel></Label>
          <Input
            type="text"
            name="entityName"
            value={formData.entityName}
            onChange={handleChange}
            placeholder="ABC Properties LLC, Family Trust, etc."
          />
        </FormGroup>

        <FormGroup>
          <Label>Create Password</Label>
          <Input
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="At least 6 characters"
            required
            minLength={6}
          />
        </FormGroup>

        <Notice>
          By requesting access, you agree to our Terms of Service and confirm that
          you are the owner or authorized manager of the property listed above.
        </Notice>

        <Button type="submit" disabled={loading}>
          {loading ? 'Submitting...' : 'Request Access'}
        </Button>

        {error && <Error>{error}</Error>}

        <LoginLink>
          Already have an account? <Link to="/login">Sign in</Link>
        </LoginLink>
      </Form>
    </Container>
  );
};

export default RegisterForm;